import { FormCrudDataGrid, FormCrudDataGridProps } from "../../../common/dataGrid/crud/FormCrudDataGrid.tsx";
import { ReclamationView } from "src/api/generated/erp/db/types/tables/reclamationView.ts";
import {
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "../../../common/dataGrid/columns.tsx";
import i18n from "i18next";
import {
	getReclamationStateLabel,
	getReclamationStateLabels,
	ReclamationState,
} from "src/api/generated/erp/db/types/enums/reclamationState.ts";
import { DocumentsOfObjectButton } from "../../documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import {
	ServerSideDataGridModel,
	ServerSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { LazySelectField } from "src/components/common/inputFields/LazySelectField.tsx";
import { RefreshableElementProps } from "src/utils/useRefreshRef.ts";
import { ReclamationForm } from "./ReclamationForm.tsx";
import { faBadgeCheck, faMoneyBill, faPaperclip } from "@fortawesome/pro-regular-svg-icons";
import { ReclamationQueryApi } from "src/api/generated/erp/reclamations/api/reclamationQueryApi.ts";
import { ReclamationsDataGridApi } from "src/api/generated/erp/reclamations/api/reclamationsDataGridApi.ts";
import { AavoSelectField } from "../../../common/inputFields/AavoSelectField.tsx";
import { LazyMultiSelectField } from "src/components/common/inputFields/LazyMultiSelectField.tsx";
import React from "react";
import { GridSortModel } from "@mui/x-data-grid-pro";
import {
	AavoDataGridMobileViewDetailsViewRenderParams,
	AavoDataGridMobileViewDetailsViewTabsConfig,
	AavoDataGridRowContextMenuParams,
} from "../../../common/dataGrid/types";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { SelectSiteField } from "src/components/views/erp/common/sites/SelectSiteField.tsx";
import { ReclamationEditApi } from "src/api/generated/erp/reclamations/api/reclamationEditApi.ts";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { CostEventsOfObjectDataGrid } from "src/components/views/erp/common/costEvents/CostEventsOfObjectDataGrid.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SingleReclamationView } from "src/components/views/erp/reclamation/singleReclamationView/SingleReclamationView.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import {
	ReclamationChangedEvent,
	ReclamationContextMenu,
} from "src/components/views/erp/reclamation/ReclamationContextMenu.tsx";
import { TextWithPhoneLinks } from "src/components/common/dataGrid/cells/TextWithPhoneLinks.tsx";
import { ServerSideDataModelResult } from "src/api/generated/common/dataGrids/serverSideDataModelResult.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import type { AgentDataGridContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";

export interface ReclamationsDataGridBaseProps
	extends RefreshableElementProps, Pick<FormCrudDataGridProps<ReclamationView>, "actionBarComponents"> {
	states?: ReclamationState[];
	fetchData?: typeof ReclamationsDataGridApi.getData;
	selectedReclamations: ReclamationView[];
	onSelectionChanged: (selectedReclamations: ReclamationView[]) => void;
	onReclamationChanged?: (event: ReclamationChangedEvent) => void;
	usePagination?: boolean;
	onDataLoaded?: (newRows: ReclamationView[]) => void;
	gridId: string;
	siteId?: number | null;
	customerOrderId?: number;
	customerId?: number;
	reclamationRoundId?: number;
	defaultSortModel?: GridSortModel;
	showResponsiblePersonFilter?: boolean;
	showDefaultActionBarFilters?: boolean;
	rowContextMenuComponents?: (params: AavoDataGridRowContextMenuParams<ReclamationView>) => React.ReactNode[];
	agentContext: AgentDataGridContext<ReclamationView>;
}

interface ReclamationsDataGridParams {
	siteId: number | null;
	reclamationRoundId: number | null;
	states: ReclamationState[];
	searchQuery: string;
	responsiblePersonId: number | null;
	reclamationLocationId: number | null;
	categoryIds: number[];
	customerOrderId?: number;
	customerId?: number;
}

export const ReclamationsDataGridBase = ({
	refreshRef,
	actionBarComponents,
	rowContextMenuComponents,
	states = [],
	fetchData = ReclamationsDataGridApi.getData,
	selectedReclamations,
	onSelectionChanged,
	onReclamationChanged,
	usePagination,
	onDataLoaded,
	gridId,
	siteId,
	customerOrderId,
	customerId,
	reclamationRoundId,
	defaultSortModel,
	showResponsiblePersonFilter = true,
	showDefaultActionBarFilters = true,
	agentContext,
}: ReclamationsDataGridBaseProps) => {
	const { defaultSiteId } = useGlobalInitData();

	return (
		<ServerSideDataGridModel<
			ServerSideDataModelResult<ReclamationView>,
			ReclamationView,
			ReclamationsDataGridParams
		>
			gridId={gridId}
			fetchData={fetchData}
			getDataModelResult={(data) => data}
			initialParams={{
				siteId: (siteId === undefined ? defaultSiteId : siteId) ?? null,
				reclamationRoundId: genericNullableValue<number>(reclamationRoundId ?? null),
				states: states,
				searchQuery: "",
				responsiblePersonId: genericNullableValue<number>(null),
				reclamationLocationId: genericNullableValue<number>(null),
				categoryIds: Array<number>(),
				customerOrderId: customerOrderId,
				customerId: customerId,
			}}
			getRowId={(row) => row.reclamationId}
			usePagination={usePagination}
			afterRefresh={({ newRows }) => {
				onDataLoaded?.(newRows);
			}}
			selectedRows={selectedReclamations}
			onSelectionChanged={onSelectionChanged}
			refreshRef={refreshRef}
			defaultGridState={
				defaultSortModel == null ? undefined : (
					{
						sorting: {
							sortModel: defaultSortModel,
						},
					}
				)
			}
			render={({ dataGridProps, currentParams, refreshData, onlySelectedRow }) => (
				<ReclamationsDataGrid
					dataGridProps={dataGridProps}
					currentParams={currentParams}
					refreshData={refreshData}
					onlySelectedRow={onlySelectedRow}
					actionBarComponents={actionBarComponents}
					rowContextMenuComponents={rowContextMenuComponents}
					states={states}
					customerOrderId={customerOrderId}
					onReclamationChanged={onReclamationChanged}
					showResponsiblePersonFilter={showResponsiblePersonFilter}
					showDefaultActionBarFilters={showDefaultActionBarFilters}
					agentContext={agentContext}
				/>
			)}
		/>
	);
};

interface ReclamationsDataGridProps extends Pick<
	ReclamationsDataGridBaseProps,
	| "actionBarComponents"
	| "rowContextMenuComponents"
	| "states"
	| "customerOrderId"
	| "onReclamationChanged"
	| "showResponsiblePersonFilter"
	| "showDefaultActionBarFilters"
	| "agentContext"
> {
	dataGridProps: ReclamationsDataGridRenderProps["dataGridProps"];
	currentParams: ReclamationsDataGridRenderProps["currentParams"];
	refreshData: ReclamationsDataGridRenderProps["refreshData"];
	onlySelectedRow: ReclamationView | undefined;
	states: ReclamationState[];
}

type ReclamationsDataGridRenderProps = Pick<
	ServerSideDataGridModelRenderProps<
		ServerSideDataModelResult<ReclamationView>,
		ReclamationView,
		ReclamationsDataGridParams
	>,
	"dataGridProps" | "currentParams" | "refreshData" | "onlySelectedRow"
>;

const ReclamationsDataGrid = ({
	dataGridProps,
	currentParams,
	refreshData,
	onlySelectedRow,
	actionBarComponents,
	rowContextMenuComponents,
	states,
	customerOrderId,
	onReclamationChanged,
	showResponsiblePersonFilter = true,
	showDefaultActionBarFilters = true,
	agentContext,
}: ReclamationsDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const canManageReclamations = hasPermission(Permissions.Reclamations.MANAGE);

	return (
		<FormCrudDataGrid<ReclamationView>
			columns={[
				integerColumn({
					field: "reclamationId",
					headerName: i18n.t("number_shortened"),
					width: 65,
					mobile: "header",
				}),
				states.length !== 1 &&
					enumColumn({
						field: "reclamationState",
						headerName: i18n.t("state"),
						enumLabels: getReclamationStateLabels(),
						mobile: "cell",
					}),
				textColumn({
					field: "title",
					headerName: i18n.t("title"),
					width: 150,
					mobile: "header",
				}),
				textColumn({
					field: "subjectCode",
					headerName: i18n.t("subject_code"),
					width: 150,
					mobile: "cell",
				}),
				textColumn({
					field: "city" as any,
					headerName: i18n.t("city"),
					width: 150,
					valueGetter: (_, row) => row.address?.city ?? (row as any).city,
					mobile: "cell",
				}),
				textColumn({
					field: "customerOrderLongName",
					headerName: i18n.t("customer_order"),
					width: 150,
					mobile: "cell",
				}),
				dateTimeColumn({
					field: "plannedCompletedDate",
					headerName: i18n.t("planned_completed_date_short"),
					width: 140,
					mobile: "cell",
				}),
				dateTimeColumn({
					field: "plannedStartAt",
					headerName: i18n.t("planned_start"),
					width: 140,
				}),
				textColumn({
					field: "reclamationCategoryName",
					headerName: i18n.t("category"),
					mobile: "cell",
				}),
				textColumn({
					field: "reclamationCauseName",
					headerName: i18n.t("cause"),
					width: 120,
				}),
				textColumn({
					field: "handlingMethodName",
					headerName: i18n.t("handling_method"),
					width: 120,
				}),
				textColumn({
					field: "reclamationLocationName",
					headerName: i18n.t("location"),
					width: 120,
				}),
				textColumn({
					field: "responsiblePersonUserName",
					headerName: i18n.t("responsible_person"),
					width: 130,
				}),
				textColumn({
					field: "workerUserName",
					headerName: i18n.t("worker"),
					width: 130,
				}),
				floatColumn({
					field: "estimatedWorkMinutes",
					headerName: i18n.t("estimated_work_hours"),
					valueGetter: (value) => (value == null ? null : value / 60),
					maxDigits: 2,
					width: 130,
				}),
				textColumn({
					field: "siteName",
					headerName: i18n.t("site"),
					width: 100,
				}),
				dateTimeColumn({
					field: "releasedAt",
					headerName: i18n.t("released_at"),
					width: 140,
				}),
				dateTimeColumn({
					field: "closedAt",
					headerName: i18n.t("closed_at"),
					width: 140,
				}),
				dateTimeColumn({
					field: "readyForReviewAt",
					headerName: i18n.t("ready_for_review_at"),
					width: 160,
				}),
				textColumn({
					field: "createdByUserName",
					headerName: i18n.t("created_by"),
					width: 130,
				}),
				dateTimeColumn({
					field: "createdAt",
					headerName: i18n.t("created_at"),
					width: 130,
				}),
				textColumn({
					field: "complainerName",
					headerName: i18n.t("complainer"),
					mobile: "cell",
				}),
				textColumn({
					field: "complainerPhone",
					headerName: i18n.t("complainer_phone_short"),
					width: 110,
					renderCell: ({ value }) => <TextWithPhoneLinks text={value} />,
				}),
				textColumn({
					field: "complainerEmail",
					headerName: i18n.t("complainer_email"),
					width: 130,
				}),
			]}
			listViewOnMobile={{
				header: (reclamation) => `${reclamation.reclamationId} | ${reclamation.title}`,
				detailsView: MobileViewDetailsView,
			}}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef("RECLAMATION", onlySelectedRow?.reclamationId)}
					/>
					{showDefaultActionBarFilters && (
						<SelectSiteField value={currentParams.siteId} onChange={(siteId) => refreshData({ siteId })} />
					)}
					{showDefaultActionBarFilters && showResponsiblePersonFilter && (
						<LazySelectField
							label={i18n.t("responsible_person")}
							fetchOptions={ReclamationQueryApi.getResponsiblePersonOptions}
							getOptionKey={(o) => o.appUserId}
							getOptionLabel={(o) => o.name}
							onChange={async (value) => {
								await refreshData({ responsiblePersonId: value });
							}}
						/>
					)}
					{showDefaultActionBarFilters && (
						<LazySelectField
							label={i18n.t("location")}
							fetchOptions={ReclamationQueryApi.getReclamationLocationOptions}
							getOptionKey={(o) => o.reclamationLocationId}
							getOptionLabel={(o) => o.name}
							defaultValue={currentParams.reclamationLocationId}
							onChange={async (value) => {
								await refreshData({ reclamationLocationId: value });
							}}
						/>
					)}
					{showDefaultActionBarFilters && (
						<AavoTextField
							label={i18n.t("search")}
							onSubmit={async (input) => {
								await refreshData({ searchQuery: input });
							}}
						/>
					)}
					{showDefaultActionBarFilters && (
						<LazyMultiSelectField
							label={i18n.t("category")}
							value={currentParams.categoryIds}
							onChange={async (value) => {
								await refreshData({ categoryIds: value });
							}}
							fetchOptions={ReclamationQueryApi.getReclamationCategoryOptions}
							getOptionKey={(o) => o.reclamationCategoryId}
							getOptionLabel={(o) => o.name}
						/>
					)}
					{showDefaultActionBarFilters && states.length > 1 && (
						<AavoSelectField
							label={i18n.t("state")}
							options={states}
							getOptionKey={(o) => o}
							onChange={async (value) => {
								await refreshData({ states: value === null ? states : [value] });
							}}
							getOptionLabel={(o) => getReclamationStateLabel(o)}
						/>
					)}
					{actionBarComponents}
				</>
			}
			actionBarMenuComponents={[
				<CustomUiActionObjectMenuItems key={"customFunctionButtons"} view={"RECLAMATIONS"} />,
			]}
			rowContextMenuComponents={(contextMenuParams) => {
				const { row, allSelectedRows } = contextMenuParams;
				return [
					<CustomUiActionObjectMenuItems
						key={"customFunctionButtons"}
						view={"RECLAMATIONS"}
						objectIds={allSelectedRows.map((reclamation) => reclamation.reclamationId)}
					/>,
					<ReclamationContextMenu
						key={"defaultReclamationContextMenu"}
						row={row}
						allSelectedRows={allSelectedRows}
						refreshData={refreshData}
						customerOrderId={customerOrderId}
						onReclamationChanged={onReclamationChanged}
					/>,
					...(rowContextMenuComponents?.(contextMenuParams) ?? []),
				];
			}}
			form={{
				dialogSize: "xl",
				dialogTitle: i18n.t("reclamation"),
				addRowEnabled: canManageReclamations,
				editEnabled: canManageReclamations,
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.reclamation.reclamation.formDialog"),
					pathSegments: row == null ? undefined : [`${i18n.t("reclamation")} ${row.reclamationId}`],
					data: {
						reclamationId: row?.reclamationId ?? null,
						customerOrderId: customerOrderId ?? null,
					},
				}),
				component: ({ row, onCompleted, onFormEdited }) => (
					<ReclamationForm
						reclamationId={row?.reclamationId}
						customerOrderId={customerOrderId}
						onFormEdited={onFormEdited}
						onCompleted={(result) => onCompleted(result)}
					/>
				),
			}}
			remove={
				!canManageReclamations ?
					{ type: "hidden" }
				:	({ row }) => ReclamationEditApi.delete_({ reclamationId: row.reclamationId })
			}
			openRow={(row) => {
				openDialog({
					title: i18n.t("reclamation"),
					size: "fullscreen",
					agentContext: {
						key: defineAgentUiContextKey("erp.reclamation.reclamation.dialog"),
						pathSegments: [`${i18n.t("reclamation")} ${row.reclamationId}`],
						data: {
							reclamationId: row.reclamationId,
							customerOrderId: customerOrderId ?? null,
						},
					},
					content: <SingleReclamationView reclamationId={row.reclamationId} />,
				});
			}}
			agentContext={agentContext}
			{...dataGridProps}
		/>
	);
};

const MobileViewDetailsView = ({
	row,
}: AavoDataGridMobileViewDetailsViewRenderParams<ReclamationView>): AavoDataGridMobileViewDetailsViewTabsConfig => {
	const { tenantConfig } = useTenantCustomizations();

	return {
		type: "tabs",
		items: [
			{
				icon: faBadgeCheck,
				title: i18n.t("tasks"),
				content: (
					<SingleSourceTasksView
						taskSourceRef={{
							sourceType: "RECLAMATION",
							sourceId: row.reclamationId,
						}}
					/>
				),
			},
			row.costEventPriceListId != null && {
				icon: faMoneyBill,
				title: i18n.t("cost_events"),
				content: (
					<CostEventsOfObjectDataGrid
						objectRef={{
							objectType: "RECLAMATION",
							objectId: row.reclamationId,
						}}
						costEventPriceListId={row.costEventPriceListId}
					/>
				),
			},
			tenantConfig.documentsEnabled && {
				icon: faPaperclip,
				title: i18n.t("documents"),
				content: (
					<DocumentsOfSingleObjectView
						objectRef={{
							objectType: "RECLAMATION",
							objectId: row.reclamationId,
						}}
						editable
						hidePreview
					/>
				),
			},
		],
	};
};
